import React from 'react';
import ReactDOM from 'react-dom';
import immutable from 'immutable';
import AutoSuggest from 'react-autosuggest';
import Divider from 'material-ui/Divider';
import Toggle from 'material-ui/Toggle';
import {componentHoc} from 'utils/mixin/componentHoc';
import ConstValues from 'utils/const';
import IconButton from 'material-ui/IconButton';
import IconButtonGroup from './IconButtonGroup';
import NavigationArrowDropDown from 'material-ui/svg-icons/navigation/arrow-drop-down';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';
import StringSearchFieldSuggestionWithCheckbox from 'components/pure/search/utils/StringSearchFieldSuggestionWithCheckbox';
import StringSearchFieldValues from './StringSearchFieldValues';
import Theme from 'styles/theme';
import 'styles/ReactAutosuggest.styl';
import cn from 'classnames';
import {getSearchFieldClass} from 'components/stateful/search/getSearchFieldClass';

/**
 * A `StringSearchField` renders a react-autosuggest powered autocomplete text field
 * for a finite / infinite string search field
 */
@componentHoc
class StringSearchField extends React.Component {
  constructor() {
    super();
    this.state = {
      // this.state.value is used internally to keep track of <AutoSuggest.value />
      value: '',
      toggled: false,
    };
    this.finiteStringOptions = immutable.List([
      {className: 'string-search-field-none-button', key: 'none'},
      {className: 'string-search-field-all-of-button', key: 'all_of'},
      {className: 'string-search-field-some-of-button', key: 'some_of'},
    ]);
    this.infiniteStringOptions = immutable.List([
      {className: 'string-search-field-none-button', key: 'not_contain'},
      {className: 'string-search-field-some-of-button', key: 'contains'},
    ]);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (typeof nextProps.toggled !== 'undefined') {
      this.setState({
        toggled: nextProps.toggled,
      });
    }
  }

  /**
   * Invoked when the component's DOM representation has been updated.
   * Scrolls to the widget if it is the most recent Search Widget to be toggled on.
   */
  componentDidUpdate() {
    if (this.props.shouldFocus) {
      this.props.scrollWidgetIntoViewIfNecessary();
      // reset the last toggled or it will keep focusing the last component on all future renders.
      this.props.updateLastToggledOnSearchWidget(null);
    }
  }

  /**
   * This callback function gets called when a `StringSearchField` is toggled on or off
   * by a user. It triggers `SearchFieldsActions.updateStringSearchExpression` in order
   * to remove / initialte the search expression / state for `this.props.searchField`
   */
  onToggleStringSearchField = (event, toggled) => {
    SearchFieldsActions.updateSearchWidgetToggleState(
      ConstValues.stringWidgetType,
      toggled,
      this.props.searchField
    );

    setTimeout(() => {
      SearchFieldsActions.updateStringSearchExpression(this.props.searchField, toggled);
    }, ConstValues.delayDurationForSearchWidgetToggles);

    if (toggled) {
      this.props.updateLastToggledOnSearchWidget(this.props.searchField.name);
    }
  };

  /**
   * Callback triggered when the user changes the value of <input /> in <AutoSuggest />
   * @param  {object} event            A react-change event
   * @param  {object} options
   * @param  {string} options.newValue A potentially valid input.value string
   * @param  {string} options.method
   *                                 'down'   - user pressed Down
   *                                 'up'     - user pressed Up
   *                                 'escape' - user pressed Escape
   *                                 'enter'  - user pressed Enter
   *                                 'click'  - user clicked (or tapped) on suggestion
   *                                 'type'  - none of the methods above (usually means that user typed something, but can also be that they pressed Backspace, pasted something into the input, etc.)
   */
  onChange = (event, options) => {
    this.setState({value: options.newValue});
  };

  /**
   * Called everytime react-autosuggest thinks it's time to update `suggestions`
   *
   * @param {string} options.value Current value of the input
   * @param {string} options.reason Current value of the input
   */
  onSuggestionsUpdateRequested = options => {
    // TODO: we don't use autosuggestion correctly, that's why we do not executing the code here
  };

  /**
   * Called everytime react-autosuggest thinks it's time to select specific value
   */
  onSuggestionSelected = event => {
    // Only finite_string search fields seem to use the auto-suggested results
    if (this.props.searchField.type === 'finite_string') {
      this.updateSuggestions(event.target.value);
    }
  };

  /**
   * Autosuggest will call this function every time you need to clear suggestions.
   */
  onSuggestionsClearRequested = () => {
    // TODO: we don't use autosuggestion correctly, that's why we do not executing the code here
  };

  /**
   * Callback for `<form/>` which wraps around the `<Autosuggestion />`. This function
   * is called everytime the user hits the "enter" key regardless of whether `this.state.value`
   * is a valid suggestion or not. This means that the user will be able to input arbitrary string
   * as a search value for `this.props.searchField`
   * @param  {SyntheticEvent} event An form.submit HTML event
   */
  onSuggestionValueSubmit = event => {
    event.preventDefault();
    if (this.props.searchField.type === 'infinite_string') {
      this.updateSuggestions(this.state.value);
    }
  };

  updateSuggestions = value => {
    this.props.onUpdateStringSearchFieldWithNewValue(this.props.searchField, value);
    setTimeout(() => {
      this.setState({
        value: '',
      });
    }, 0);
  };

  renderSuggestion = suggestion => {
    const isSelected = this.isSuggestionSelected(suggestion);
    if (isSelected) {
      return (
        <StringSearchFieldSuggestionWithCheckbox
          label={suggestion}
          value={suggestion}
          checked={true}
        />
      );
    } else {
      return (
        <StringSearchFieldSuggestionWithCheckbox
          label={suggestion}
          value={suggestion}
          checked={false}
        />
      );
    }
  };

  /**
   * Return `true` if `suggestion` is already selected; false otherwise
   * @param  {string}  suggestion A suggestion string to be rendered in the suggestion `listbox`
   * @return {Boolean}            `true` if `suggestion` is already selected; `false` otherwise
   */
  isSuggestionSelected = suggestion => {
    const selectedValues = this.props.selectedStringValues;
    return selectedValues && selectedValues.includes(suggestion);
  };

  /**
   * Return an array of suggestions for `this.props.searchField`
   */
  getSuggestions = () => {
    if (this.props.searchField.type === 'finite_string') {
      let suggestions = this.props.searchField.suggestions;
      if (this.state.value) {
        suggestions = suggestions.filter(suggestion => {
          const value = this.state.value.toLowerCase();
          return suggestion.toLowerCase().indexOf(value) > -1;
        });
      }
      const sortedSuggestions = suggestions.sort(); // Alphabetical
      return sortedSuggestions.toArray();
    }
    return [];
  };

  /**
   * Call `this.props.onUpdateSelectedOptionKey` to update
   * `SearchFieldsStore.getState().get('searchFieldsState')` and
   * `SearchFieldsStore.getState().get('searchExpression')` using `this.props.searchField`
   * and `key`
   * @param {string} key An `IconButton` option key
   */
  onTouchTapIconButton = key => {
    this.props.onUpdateSelectedOptionKey(this.props.searchField, key);
  };

  /**
   * User clicks the "x" button at the right of a `StringSearchFieldValueTag`. We should
   * call `SearchFieldsActions.removeStringSearchFieldSelectedValue` in order to remove this
   * value from the `SearchFieldsStore.getState().get('searchExpression')`
   * @param  {string} value A string value to be removed
   */
  onTouchTapValueTagRemoveButton = value => {
    SearchFieldsActions.removeStringSearchFieldSelectedValue(this.props.searchField, value);
  };

  /**
   * User clicks the dropdown button for the <AutoSuggest /> component. We make <AutoSuggest />
   * gain focus so that the auto-completion dropdown will be shown
   */
  onTouchTapAutoSuggestDropdownButton = () => {
    const domNode = ReactDOM.findDOMNode(this.autoSuggest);
    if (domNode) {
      domNode.children[0].focus();
    }
  };

  render() {
    const inputProps = {
      value: this.state.value ?? '',
      placeholder: 'Search...',
      onChange: this.onChange,
    };
    const classes = cn('string-search-widget', getSearchFieldClass(this.props.searchField.name));
    return (
      <div className={classes}>
        <Toggle
          label={this.props.searchField.name}
          defaultToggled={false}
          onToggle={this.onToggleStringSearchField}
          toggled={this.props.toggled}
        />
        <Divider style={{marginTop: '5px'}} />

        {(() => {
          if (this.state.toggled) {
            if (this.props.searchField.type === 'finite_string') {
              return (
                <IconButtonGroup
                  buttonOptions={this.finiteStringOptions}
                  selectedOptionKey={this.props.selectedOptionKey}
                  onTouchTapIconButton={this.onTouchTapIconButton}
                />
              );
            } else if (this.props.searchField.type === 'infinite_string') {
              return (
                <IconButtonGroup
                  buttonOptions={this.infiniteStringOptions}
                  selectedOptionKey={this.props.selectedOptionKey}
                  onTouchTapIconButton={this.onTouchTapIconButton}
                />
              );
            }
          }
        })()}

        {(() => {
          if (this.state.toggled) {
            return (
              <div>
                <span className="center-label">{this.props.selectedOptionLabel}</span>
              </div>
            );
          }
        })()}

        {(() => {
          if (this.state.toggled) {
            const suggestions = this.getSuggestions();
            return (
              <div className="string-search-auto-suggest">
                <form onSubmit={this.onSuggestionValueSubmit}>
                  <AutoSuggest
                    ref={c => (this.autoSuggest = c)}
                    inputProps={inputProps}
                    suggestions={suggestions}
                    renderSuggestion={this.renderSuggestion}
                    getSuggestionValue={s => s.label}
                    onSuggestionSelected={this.onSuggestionSelected}
                    onSuggestionsFetchRequested={this.onSuggestionsUpdateRequested}
                    onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                  />

                  {(() => {
                    if (this.props.searchField.type === 'finite_string') {
                      return (
                        <IconButton
                          className="auto-suggest-dropdown-button"
                          onClick={this.onTouchTapAutoSuggestDropdownButton}>
                          <NavigationArrowDropDown color={Theme.palette.actionButtonColor} />
                        </IconButton>
                      );
                    }
                  })()}
                </form>
              </div>
            );
          }
        })()}

        {(() => {
          if (this.state.toggled) {
            return (
              <StringSearchFieldValues
                values={this.props.selectedStringValues}
                onTouchTapValueTagRemoveButton={this.onTouchTapValueTagRemoveButton}
              />
            );
          }
        })()}
      </div>
    );
  }
}

export default StringSearchField;
